import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LogOut,
  LayoutGrid,
  Calendar,
  MessageCircle,
  Users,
  User,
  Shield,
  GraduationCap,
  Newspaper,
  Wrench,
  FileText,
  PlayCircle,
  ChevronDown,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Avatar from "./Avatar";
import DonateButton from "./DonateButton";
import SuggestionButton from "./SuggestionButton";

const BASE_LINKS = [
  { path: "/board",   label: "Board",      Icon: LayoutGrid,    testId: "nav-board" },
  { path: "/events",  label: "Events",     Icon: Calendar,      testId: "nav-events" },
  {
    path: "/news", label: "News", Icon: Newspaper, testId: "nav-news",
    children: [
      { path: "/news",        label: "Artikel", Icon: FileText,    testId: "nav-news-articles", desc: "Kuratierte KI-Artikel aus t3n, all-ai, netzpolitik" },
      { path: "/news/videos", label: "Videos",  Icon: PlayCircle,  testId: "nav-news-videos",   desc: "Neueste YouTube-Videos ausgewählter Kanäle" },
    ],
  },
  { path: "/lernen",  label: "KI-Lernen",  Icon: GraduationCap, testId: "nav-lernen" },
  { path: "/tools",   label: "AI Tools",   Icon: Wrench,        testId: "nav-tools" },
  { path: "/chat",    label: "Chat",       Icon: MessageCircle, testId: "nav-chat" },
  { path: "/members", label: "Mitglieder", Icon: Users,         testId: "nav-members" },
  { path: "/profile", label: "Profil",     Icon: User,          testId: "nav-profile" },
];

function NavLinkWithDropdown({ link, location }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const wrapRef = useRef(null);
  const { Icon, label, testId, path, children } = link;
  const active = location.pathname === path || children.some((c) => location.pathname === c.path);

  const openMenu = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  // Close on outside click (Mobile / Tap)
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      data-testid={`${testId}-wrap`}
    >
      <Link
        to={path}
        data-testid={testId}
        onClick={(e) => {
          // On mobile (coarse pointer) first tap opens the dropdown instead of navigating
          if (window.matchMedia("(hover: none)").matches && !open) {
            e.preventDefault();
            setOpen(true);
          } else {
            setOpen(false);
          }
        }}
        className={`relative group/link flex items-center gap-1.5 px-2 lg:px-3 h-10 whitespace-nowrap font-outfit font-medium text-[13px] tracking-tight transition-all duration-200 ${
          active ? "text-[#00e5ff]" : "text-white/60 hover:text-white"
        }`}
      >
        <Icon size={16} strokeWidth={active ? 2.2 : 1.8} />
        <span className="hidden lg:inline">{label}</span>
        <ChevronDown
          size={12}
          className={`text-white/40 group-hover/link:text-white/70 transition-transform duration-200 hidden lg:inline ${open ? "rotate-180" : ""}`}
        />
        <span
          className={`absolute left-2 right-2 lg:left-3 lg:right-3 bottom-0 h-px transition-all duration-300 ${
            active
              ? "bg-[#00e5ff] opacity-100"
              : "bg-white opacity-0 group-hover/link:opacity-20"
          }`}
        />
      </Link>
      {open && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-[60]"
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
          data-testid={`${testId}-dropdown`}
        >
          <div className="relative min-w-[280px] border border-white/10 bg-black/95 backdrop-blur-xl shadow-2xl shadow-black/60">
            <div className="absolute -top-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 border-t border-l border-white/10 bg-black/95 rotate-45" />
            <div className="relative p-1.5">
              {children.map((c) => {
                const isActive = location.pathname === c.path;
                const ChildIcon = c.Icon;
                return (
                  <Link
                    key={c.path}
                    to={c.path}
                    data-testid={c.testId}
                    onClick={() => setOpen(false)}
                    className={`flex items-start gap-3 px-3.5 py-3 transition-colors ${
                      isActive ? "bg-[#00e5ff]/[0.06]" : "hover:bg-white/[0.04]"
                    }`}
                  >
                    <div className={`shrink-0 w-8 h-8 border flex items-center justify-center mt-0.5 ${
                      isActive ? "border-[#00e5ff]/50 bg-[#00e5ff]/10" : "border-white/10 bg-white/[0.02]"
                    }`}>
                      <ChildIcon size={14} className={isActive ? "text-[#00e5ff]" : "text-white/70"} />
                    </div>
                    <div className="min-w-0">
                      <div className={`font-outfit text-[13px] font-semibold leading-tight ${
                        isActive ? "text-[#00e5ff]" : "text-white"
                      }`}>
                        {c.label}
                      </div>
                      <div className="text-[11.5px] text-white/40 mt-0.5 leading-snug">
                        {c.desc}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || "";
        const { data } = await axios.get(`${API}/api/events`, { withCredentials: true });
        const now = new Date();
        const upcoming = (data || []).filter(e => new Date(e.date || e.start_date || e.created_at) >= now);
        setUpcomingEvents(upcoming.length);
      } catch {}
    };
    if (user) fetchEvents();
  }, [user, location.pathname]);

  const links = BASE_LINKS;
  const isAdmin = user?.role === "admin";

  return (
    <nav
      data-testid="navbar"
      className={`sticky top-0 z-40 transition-all duration-300 border-b backdrop-blur-xl ${
        scrolled
          ? "bg-black/80 border-white/[0.08]"
          : "bg-black/50 border-white/[0.04]"
      }`}
    >
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-6">
        <div className="flex items-center justify-between h-16 sm:h-[72px] gap-3">
          {/* ===== Logo ===== */}
          <Link
            to="/board"
            className="relative z-20 flex items-center gap-2 group/logo shrink-0 bg-[#0a0a0a] pr-3"
            data-testid="navbar-logo"
          >
            <span className="font-outfit font-black text-lg sm:text-xl tracking-tighter">
              <span className="text-[#00e5ff]">[KI]</span>
              <span className="text-white group-hover/logo:text-white/80 transition-colors">NPUT</span>
            </span>
            <span className="hidden xl:flex items-center gap-1.5 px-1.5 py-0.5 border border-[#00e5ff]/20 bg-[#00e5ff]/5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00e5ff] opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#00e5ff]" />
              </span>
              <span className="text-[9px] uppercase tracking-[0.22em] text-white/55 font-outfit font-medium">
                Live
              </span>
            </span>
          </Link>

          {/* ===== Links ===== */}
          <div className="flex items-center gap-0 sm:gap-0.5 overflow-x-auto lg:overflow-visible scrollbar-hide flex-1 justify-center min-w-0">
            {links.map((link) => {
              if (link.children) {
                return <NavLinkWithDropdown key={link.path} link={link} location={location} />;
              }
              const { path, label, Icon, testId, soon } = link;
              const active = location.pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  data-testid={testId}
                  title={label + (soon ? " (in Entwicklung)" : "")}
                  className={`relative group/link flex items-center gap-1.5 px-2 lg:px-3 h-10 whitespace-nowrap font-outfit font-medium text-[13px] tracking-tight transition-all duration-200 ${
                    active
                      ? "text-[#00e5ff]"
                      : soon
                        ? "text-white/30 hover:text-white/55"
                        : "text-white/60 hover:text-white"
                  }`}
                >
                  <Icon size={16} strokeWidth={active ? 2.2 : 1.8} />
                  <span className="hidden lg:inline">{label}</span>
                  {path === "/events" && upcomingEvents > 0 && (
                    <span className="px-1.5 text-[10px] leading-[16px] font-bold text-black bg-[#00e5ff] rounded-full min-w-[18px] text-center">{upcomingEvents}</span>
                  )}
                  {soon && (
                    <span className="hidden xl:inline-block ml-0.5 px-1 py-0 text-[8.5px] leading-[14px] uppercase tracking-[0.18em] font-bold text-[#00e5ff]/80 bg-[#00e5ff]/10 border border-[#00e5ff]/20">Soon</span>
                  )}
                  <span
                    className={`absolute left-2 right-2 lg:left-3 lg:right-3 bottom-0 h-px transition-all duration-300 ${
                      active
                        ? "bg-[#00e5ff] opacity-100"
                        : "bg-white opacity-0 group-hover/link:opacity-20"
                    }`}
                  />
                </Link>
              );
            })}
          </div>

          {/* ===== User / Actions ===== */}
          <div className="relative z-20 flex items-center gap-1 sm:gap-2 shrink-0 bg-[#0a0a0a] pl-2">
            <SuggestionButton />
            <DonateButton />
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                title="Admin-Bereich"
                className={`group flex items-center gap-1.5 px-2 md:px-3 h-9 text-[12.5px] font-outfit font-semibold tracking-tight border transition-all duration-200 ${
                  location.pathname === "/admin"
                    ? "text-[#00e5ff] border-[#00e5ff] bg-[#00e5ff]/15"
                    : "text-white/80 border-white/20 hover:text-white hover:border-white/50 hover:bg-white/5"
                }`}
              >
                <Shield size={14} />
                <span className="hidden md:inline">Admin</span>
              </Link>
            )}
            <div className="hidden md:flex items-center gap-2.5" data-testid="navbar-user">
              <Avatar src={user?.avatar_url} name={`${user?.first_name || ""} ${user?.last_name || ""}`} size={32} testId="navbar-avatar" />
              <div className="leading-tight hidden 2xl:block">
                <div className="text-[13px] text-white/90 font-outfit font-semibold tracking-tight">
                  {user?.first_name}
                </div>
                {user?.role === "admin" && (
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#00e5ff]/80 font-outfit font-semibold">
                    Admin
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              title="Abmelden"
              className="group/logout flex items-center gap-1.5 px-3 h-10 text-[13px] font-outfit font-medium tracking-tight text-white/55 hover:text-[#00e5ff] border border-transparent hover:border-[#00e5ff]/30 hover:bg-[#00e5ff]/5 transition-all duration-200"
            >
              <LogOut size={14} />
              <span className="hidden lg:inline">Abmelden</span>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </nav>
  );
}
